/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { AxiosResponse, AxiosError } from 'axios';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { differenceInDays, format, intlFormatDistance, Locale } from 'date-fns';
import { de, es, fr, it, ja, ko, vi, zhCN, enUS } from 'date-fns/locale';
import { openDB } from 'idb';
import { Collection, Resource } from '@/types/library';
import { ResourceType } from './const';
import { Agent, AgentTypeEnum } from '@/types/agent';
import { CitationFile } from '@/types/citation';
import { lazy } from 'react';

const LocaleMap: { [key: string]: Locale } = {
  de,
  en: enUS,
  es,
  fr,
  it,
  ja,
  ko,
  vi,
  zh: zhCN, // Using Simplified Chinese as default Chinese locale
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export enum ERequestStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  RELOADING = 'reloading',
  FAILED = 'failed',
}
export const createCustomAppError = (message: string, detail: any, status: any) => {
  const error = new Error(message);
  error.name = 'CustomAppError';
  (error as any).detail = detail;
  (error as any).status = status; // Attach custom detail property
  return error;
};

export const handleExternalError = <T, V>(func: (args: T) => Promise<AxiosResponse<V>>) => {
  return async (args: T): Promise<V> => {
    try {
      const res = await func(args);
      return res.data; // Return only the data from the Axios response
    } catch (ex: unknown) {
      // Catch and handle the error
      if (ex instanceof AxiosError) {
        let errorMessage = 'An error occurred';

        // Try to extract error message from different FastAPI response formats
        if (ex.response?.data?.detail) {
          if (typeof ex.response.data.detail === 'string') {
            errorMessage = ex.response.data.detail;
          } else if (Array.isArray(ex.response.data.detail)) {
            // Handle validation errors array
            errorMessage = ex.response.data.detail
              .map((err: any) => err.msg || err.message || JSON.stringify(err))
              .join(', ');
          } else {
            errorMessage = JSON.stringify(ex.response.data.detail);
          }
        } else if (ex.response?.data?.message) {
          errorMessage = ex.response.data.message;
        } else if (ex.message) {
          errorMessage = ex.message;
        }

        const errorDetail = ex.response?.data || ex.response || null;
        const errorStatus = ex.response?.status || 500; // Default to 500 if status is not available

        // Log the error details for debugging (optional)
        console.log('Axios Error Details:', errorDetail);

        // Create and throw a custom error with a message and detail
        throw createCustomAppError(`Error ${errorStatus}: ${errorMessage}`, errorDetail, errorStatus);
      } else {
        // For non-Axios errors (like network issues), fallback to a generic error message
        const errorMessage = ex instanceof Error ? ex.message : 'Unknown error occurred.';
        console.log('Unknown error:', ex);
        throw createCustomAppError(errorMessage, null, null); // No additional details
      }
    }
  };
};

export const formatDateString = (
  dateStr: string,
  { dateFormat = 'yyyy-MM-dd', locale = 'en' }: { dateFormat?: string; locale?: keyof typeof LocaleMap }
): string => {
  if (dateStr) {
    const exactDate = new Date(dateStr);
    const localeObj = LocaleMap[locale] ?? LocaleMap['en'];
    if (differenceInDays(new Date(), exactDate) >= 7) {
      let curDateFormat = dateFormat;

      if (exactDate.getFullYear() === new Date().getFullYear()) {
        curDateFormat = curDateFormat.replace(', yyyy', '');
      }
      return format(exactDate, curDateFormat, { locale: localeObj });
    }

    const formattedDistance = intlFormatDistance(exactDate, new Date(), {
      locale: localeObj.code,
    });
    return formattedDistance === 'now' ? 'Just now' : formattedDistance;
  } else return 'Time Value Unknown';
};

export const formatDateToCustomString = (
  dateStr: string,
  {
    dateFormat = 'MMM dd, yyyy, hh:mm a', // Desired format
    locale = 'en',
  }: { dateFormat?: string; locale?: keyof typeof LocaleMap }
): string => {
  const exactDate = new Date(dateStr);
  const localeObj = LocaleMap[locale] ?? LocaleMap['en'];

  // Format the date using the specified format
  return format(exactDate, dateFormat, { locale: localeObj });
};

export const getFileSize = (size: number) => {
  const sizeInKB = size / 1024;
  const sizeInMB = sizeInKB / 1024;
  const sizeInGB = sizeInMB / 1024;
  if (sizeInGB >= 1) {
    return `${sizeInGB?.toFixed(2)} GB`;
  }
  if (sizeInMB >= 1) {
    return `${sizeInMB?.toFixed(2)} MB`;
  }
  if (sizeInKB >= 1) {
    return `${sizeInKB?.toFixed(2)} KB`;
  }
  return `${size?.toFixed(2)} B`;
};

export const fetchAndCacheFileBlob = async (
  fileUrl: string,
  DB_NAME: string = 'dxa_reference',
  STORE_NAME: string = 'pdfs'
): Promise<string> => {
  const db = await openDB(DB_NAME, 1, {
    upgrade(db) {
      db.createObjectStore(STORE_NAME);
    },
  });

  // Extract cache key from URL path
  const urlPath = new URL(fileUrl).pathname;
  const cacheKey = urlPath || fileUrl;

  const cachedData = await db.get(STORE_NAME, cacheKey);

  if (cachedData) {
    const objectUrl = URL.createObjectURL(cachedData);

    return objectUrl;
  } else {
    // Fetch the PDF, cache it in IndexedDB
    const response = await fetch(fileUrl);
    const blob = await response.blob();

    // Store the PDF Blob in IndexedDB
    await db.put(STORE_NAME, blob, cacheKey);

    const objectUrl = URL.createObjectURL(blob);
    return objectUrl;
  }
};

export const groupResourcesByCollection = (
  resources: Resource[] = [],
  collections: Collection[] = []
): Collection[] => {
  if (!resources?.length) {
    return collections.map(collection => ({
      ...collection,
      resources: [],
    }));
  }

  const noCollectionResources = resources.filter(resource => !resource?.collection?.collection_id);

  const collectionResources = resources.filter(resource => resource?.collection?.collection_id);
  const groupedCollectionResources = collectionResources.reduce(
    (acc, resource) => {
      const collectionId = resource?.collection?.collection_id;

      if (collectionId && !acc[collectionId]) {
        const collection = collections.find(collection => collection.collection_id === collectionId);
        if (collection) {
          acc[collectionId] = {
            ...collection,
            collection_id: collectionId,
            resources: [],
          };
        }
      }

      if (collectionId && acc[collectionId]) {
        //@ts-ignore
        acc[collectionId].resources.push(resource);
      }

      return acc;
    },
    {} as Record<string, Collection>
  );

  Object.values(groupedCollectionResources).forEach(collection => {
    collection.resources?.sort((a: Resource, b: Resource) => {
      if (!a.resource_type) return 1;
      if (!b.resource_type) return -1;
      return a.resource_type.localeCompare(b.resource_type);
    });
  });

  noCollectionResources.sort((a, b) => {
    if (!a.resource_type) return 1;
    if (!b.resource_type) return -1;
    return a.resource_type.localeCompare(b.resource_type);
  });

  const result = [
    ...collections.map(collection => ({
      ...collection,
      resources: (collection?.collection_id && groupedCollectionResources?.[collection.collection_id]?.resources) || [],
    })),
    ...noCollectionResources.map(resource => ({
      ...resource,
      resources: [resource],
    })),
  ];

  return result as unknown as Collection[];
};

export const groupResourcesByCollectionAgentForm = (resources: Resource[] = [], collections: Collection[] = []) => {
  const noCollectionResources = resources?.filter(resource => !resource?.collection?.collection_id);
  const collectionResources = resources?.filter(resource => resource?.collection?.collection_id);

  const groupedCollectionResources = collectionResources?.reduce(
    (acc, resource) => {
      const collectionId = resource?.collection?.collection_id;

      if (collectionId && !acc[collectionId]) {
        const collection = collections?.find(collection => collection.collection_id === collectionId);
        if (collection) {
          acc[collectionId] = {
            ...collection,
            collection_id: collectionId,
            collection: collection,
            resources: [],
          };
        }
      }

      if (collectionId && acc[collectionId]) {
        acc[collectionId].resources.push(resource);
      }

      return acc;
    },
    {} as Record<string, Collection | any>
  );

  // Sort resources within each collection by type
  if (groupedCollectionResources) {
    Object.values(groupedCollectionResources).forEach(collection => {
      collection?.resources?.sort((a: Resource, b: Resource) => {
        if (!a?.resource_type) return 1;
        if (!b?.resource_type) return -1;
        return a.resource_type.localeCompare(b.resource_type);
      });
    });
  }

  noCollectionResources?.sort((a, b) => {
    if (!a?.resource_type) return 1;
    if (!b?.resource_type) return -1;
    return a.resource_type.localeCompare(b.resource_type);
  });

  const result = [
    ...(groupedCollectionResources ? Object.values(groupedCollectionResources) : []),
    ...noCollectionResources?.map(resource => ({
      ...resource,
    })),
  ];
  return result;
};

export const flattenResourceData = (resources: any[]) => {
  return resources
    ?.map(resource => {
      // Handle case where resource might be undefined or have different structure
      if (!resource) return null;

      // If resource already has resource_type, it's already flattened or a direct resource
      if (resource.resource_type) {
        return resource;
      }

      // If resource has a nested resource property, flatten it
      if (resource.resource) {
        return {
          ...resource,
          id: resource?.resource?.id,
          resource_id: resource?.resource?.resource_id,
          name: resource?.resource?.name,
          description: resource?.resource?.description,
          resource_type: resource?.resource?.resource_type,
          action: resource?.resource?.data?.action,
          condition: resource?.resource?.data?.condition,
        };
      }

      // Fallback: return resource as is if no nested structure found
      return resource;
    })
    .filter(Boolean); // Remove null entries
};

export const getResourceType = (resourceType: string, t: (key: string) => string) => {
  let displayType = resourceType;
  if (!resourceType) {
    displayType = t('resource_type.topic');
  } else if (resourceType === ResourceType.FILE) {
    displayType = t('resource_type.file');
  } else if (resourceType === ResourceType.INTERVIEW) {
    displayType = t('resource_type.interview');
  } else if (resourceType === ResourceType.PLAN) {
    displayType = t('resource_type.plan');
  } else if (resourceType === ResourceType.PRIO_KNOWLEDGE) {
    displayType = t('resource_type.prior_knowledge');
  } else if (resourceType === ResourceType.EXPERIENTIAL) {
    displayType = t('resource_type.expert_guidance');
  } else if (resourceType === ResourceType.MCP) {
    displayType = t('resource_type.mcp_server');
  } else if (resourceType === ResourceType.KNOWLEDGE_BASE) {
    displayType = t('resource_type.knowledge_base');
  }
  return displayType;
};

export const processCitationFiles = (sources: any, currentAgent: Agent, userId: number): CitationFile[] => {
  // Handle added_knowledge structure
  if (sources.question && sources.answer && sources.resources) {
    // This is an added_knowledge object
    return sources.resources
      .map((resource: any) => {
        const matchingResource = currentAgent?.agent_resources?.find(
          agent_resource => agent_resource.resource.name === resource.name
        );

        if (matchingResource) {
          return {
            ...resource,
            page: resource.page_index != null ? [resource.page_index + 1] : [1],
            type: 'resource',
            source: matchingResource.resource.data?.s3_key,
            extension: matchingResource.resource.name.split('.').at(-1),
          };
        } else {
          // If no matching resource found, still return the resource with basic info
          console.log('No matching resource found for:', resource.name, 'but still including it');
          return {
            ...resource,
            page: resource.page_index != null ? [resource.page_index + 1] : [1],
            type: 'resource',
            source:
              resource.data?.s3_key ||
              `aiKO/uploaded_folders/${userId}/agent/${currentAgent?.agent_id || currentAgent?.id}/` + resource.name,
            extension: resource.name.split('.').at(-1),
          };
        }
      })
      .filter((file: any) => file !== null) as CitationFile[];
  }

  if (sources.resources && Array.isArray(sources.resources)) {
    return sources.resources
      .map((resource: any) => {
        const matchingResource = currentAgent?.agent_resources?.find(
          agent_resource => agent_resource.resource.name === resource.name
        );

        if (matchingResource) {
          return {
            ...resource,
            page: resource.page_index != null ? [resource.page_index + 1] : [1],
            type: 'resource',
            source: matchingResource.resource.data?.s3_key,
            extension: matchingResource.resource.name.split('.').at(-1),
          };
        } else {
          // If no matching resource found, still return the resource with basic info
          return {
            ...resource,
            page: resource.page_index != null ? [resource.page_index + 1] : [1],
            type: 'resource',
            source:
              resource.data?.s3_key ||
              `aiKO/uploaded_folders/${userId}/agent/${currentAgent?.agent_id || currentAgent?.id}/` + resource.name,
            extension: resource.name.split('.').at(-1),
          };
        }
      })
      .filter((file: any) => file !== null) as CitationFile[];
  }
  if (!Array.isArray(sources)) {
    sources = sources?.retrievals ?? [];
  }
  const agentUserId = currentAgent?.user_id;
  const agentId = currentAgent?.agent_id ?? currentAgent?.id?.toString() ?? '';
  if (!sources) {
    return [];
  }

  // Use a Map to group files by name and collect all pages
  const fileMap = new Map<string, CitationFile>();

  sources.forEach((file: any) => {
    const { source, page_index: pages, type, retrievals, URL, resources, file_path, source_path } = file;

    if (URL) {
      const key = URL;
      if (!fileMap.has(key)) {
        fileMap.set(key, {
          name: URL,
          page: [1],
          extension: '',
          source: URL,
          type: 'url',
        });
      }
      return;
    }

    // Handle adaptive agent citation format: { source_type, file_name, page_index, chunk_indices }
    if (file.file_name && file.source_type === 'document') {
      const fileName = file.file_name;
      const fileExtension = fileName.split('.').at(-1) ?? '';
      const fullS3Path = `aiKO/uploaded_folders/${agentUserId ?? userId ?? ''}/agent/${agentId ?? ''}/` + fileName;
      const pageNumber = file.page_index != null ? Number(file.page_index) + 1 : 1;
      const key = fileName;

      if (!fileMap.has(key)) {
        fileMap.set(key, {
          name: fileName,
          page: [pageNumber],
          extension: fileExtension,
          source: fullS3Path,
        });
      } else {
        // Merge pages for existing file
        const existing = fileMap.get(key)!;
        existing.page = [...new Set([...existing.page, pageNumber])];
      }
      return;
    }

    if (retrievals) {
      const retrievedFiles = processCitationFiles({ retrievals, resources }, currentAgent, userId);
      retrievedFiles.forEach(retrievedFile => {
        const key = retrievedFile.name;
        if (!fileMap.has(key)) {
          fileMap.set(key, retrievedFile);
        } else {
          // Merge pages
          const existing = fileMap.get(key)!;
          existing.page = [...new Set([...existing.page, ...retrievedFile.page])];
        }
      });
    }

    if (source) {
      const file_name = source.split('/').at(-1);
      const fileExtension = source.split('.').at(-1);
      let fileAgentId = agentId;
      if (currentAgent?.agent_type === AgentTypeEnum.GENERAL) {
        fileAgentId = file_path?.split('/').at(-2);
      }
      let fullS3Path = '';
      if (source_path && source_path !== '') {
        fullS3Path = source_path;
      } else {
        fullS3Path = `aiKO/uploaded_folders/${agentUserId ?? userId ?? ''}/agent/${fileAgentId ?? ''}/` + file_name;
      }
      if (!file_name) return;

      const key = file_name;

      if (type === 'txt' || type === 'doc' || type === 'docx' || type === 'html') {
        if (!fileMap.has(key)) {
          fileMap.set(key, {
            name: file_name,
            page: [1],
            extension: fileExtension ?? '',
            source: fullS3Path,
          });
        }
        return;
      }

      // Handle pages - collect all pages for the same file
      // Backend sends 0-based page indices, convert to 1-based for UI
      let pageNumbers: number[] = [];
      if (pages != null) {
        if (Array.isArray(pages)) {
          pageNumbers = pages.map(p => Number(p) + 1);
        } else {
          pageNumbers = [Number(pages) + 1];
        }
      } else {
        pageNumbers = [1]; // Default to page 1 if no pages specified
      }

      if (!fileMap.has(key)) {
        fileMap.set(key, {
          name: file_name,
          page: pageNumbers,
          extension: fileExtension ?? '',
          source: fullS3Path,
        });
      } else {
        // Merge pages for existing file
        const existing = fileMap.get(key)!;
        existing.page = [...new Set([...existing.page, ...pageNumbers])];
      }
      return;
    }

    if (typeof file === 'string') {
      const parts = file.split('/');
      if (parts.length > 0) {
        const file_name = parts.at(-1);
        const extension = file_name?.split('.').at(-1);

        if (file_name) {
          // Find file in agent resources of current agent
          const agentResources = currentAgent?.agent_resources || [];
          const matchingResource = agentResources.find(agent_resource => agent_resource.resource.name === file_name);

          if (matchingResource) {
            const key = file_name;
            if (!fileMap.has(key)) {
              fileMap.set(key, {
                name: file_name,
                page: [1],
                extension: extension ?? '',
                source: `aiKO/uploaded_folders/${agentUserId ?? userId ?? ''}/agent/${agentId ?? ''}/` + file_name,
              });
            }
          }
        }
      }
    }

    if (Array.isArray(file.resources)) {
      file.resources?.forEach((e: any) => {
        const fileExtension = e?.name?.split('.').at(-1);
        const key = e?.name;

        if (!fileMap.has(key)) {
          fileMap.set(key, {
            name: e?.name,
            page: e?.page_index != null ? [e?.page_index + 1] : [1],
            extension: fileExtension ?? '',
            source: `aiKO/uploaded_folders/${agentUserId ?? userId ?? ''}/agent/${agentId ?? ''}/` + e?.name,
          });
        } else {
          // Merge pages for existing file
          const existing = fileMap.get(key)!;
          const newPages = e?.page_index != null ? [e?.page_index + 1] : [1];
          existing.page = [...new Set([...existing.page, ...newPages])];
        }
      });
    }
  });

  // Convert Map to array and sort pages
  const result = Array.from(fileMap.values()).map(file => ({
    ...file,
    page: file.page.sort((a, b) => a - b), // Sort pages numerically
    pages: file.page.sort((a, b) => a - b), // Also set pages property for compatibility
  }));

  return result;
};

export const lazyWithRetry = (componentImport: () => any) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();

      window.localStorage.setItem('page-has-been-force-refreshed', 'false');

      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // This handles JS chunk loading failures when a new deployment occurs
        // and the requested chunk no longer exists on the server
        window.localStorage.setItem('page-has-been-force-refreshed', 'true');
        return window.location.reload();
      }

      // If we've already tried refreshing once and still getting errors,
      // let the application crash with the actual error
      throw error;
    }
  });

export const commonFeatureToggle = {
  CAPTURE: 'capture',
  AGENT_LEVEL: 'agent_level',
  SHOW_DEMO_AGENT: 'show_demo_agent',
  TOOL_HUB: 'tool_hub',
  CAPTURE_V2: 'capture_v2',
  KNOWLEDGE_BASE: 'knowledge_base',
};

export const workspaceFeatureToggle = {
  ...commonFeatureToggle,
  BETA_FEATURES: 'beta_features',
};

export const userFeatureToggle = {
  ...commonFeatureToggle,
  CREATE_WORKSPACE: 'create_workspace',
  PUSH_TO_MARKETPLACE: 'push_to_marketplace',
};

export const featureMappingName = {
  CAPTURE: 'EXPERT INTERVIEW (SHARING SESSION)',
  AGENT_LEVEL: 'AUTONOMOUS AGENT',
  BETA_FEATURES: 'DEVELOPMENT MODE',
  SHOW_DEMO_AGENT: 'DEMO AGENT',
  TOOL_HUB: 'TOOL HUB',
  CREATE_WORKSPACE: 'CREATE WORKSPACE',
  CAPTURE_V2: 'CAPTURE w/ ProSEA',
  KNOWLEDGE_BASE: 'KNOWLEDGE BASE',
  PUSH_TO_MARKETPLACE: 'PUSH TO MARKETPLACE',
};

/**
 * Safely serializes an object to JSON string, handling circular references and non-serializable objects
 * @param obj - The object to serialize
 * @param space - Number of spaces for indentation (default: 2)
 * @returns JSON string representation of the object
 */
export function safeJsonStringify(obj: any, space: number = 2): string {
  try {
    const seen = new WeakSet();
    const replacer = (_: string, value: any) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }

      // Handle special cases
      if (value === undefined) {
        return '[undefined]';
      }
      if (typeof value === 'function') {
        return '[Function]';
      }
      if (value instanceof Date) {
        return value.toISOString();
      }
      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack,
        };
      }
      if (value instanceof RegExp) {
        return value.toString();
      }
      if (value instanceof Map) {
        return Object.fromEntries(value);
      }
      if (value instanceof Set) {
        return Array.from(value);
      }

      return value;
    };

    return JSON.stringify(obj, replacer, space);
  } catch (error) {
    console.error('Error serializing object to JSON:', error);
    return JSON.stringify(
      {
        error: 'Failed to serialize object',
        message: error instanceof Error ? error.message : 'Unknown error',
        originalObject: String(obj),
      },
      null,
      space
    );
  }
}
